import { useLocation, useNavigate, Link } from 'react-router-dom';
import { ROUTES } from 'router/routes';
import { useEffect } from 'react';
import { TicketHolder } from './TicketHolder/TicketHolder';
// import { GameInfo } from './GameInfo/GameInfo';
import { Grid, GridItem, VStack, Show, Spinner } from '@chakra-ui/react';
import { useOrderSummaryQuery } from './hooks/useOrderSummaryQuery';
import { OrderDetails } from './OrderDetails/OrderDetails';
import { LoadingView } from './LoadingView/LoadingView';
import { SuccessHeader } from './SuccessHeader/SuccessHeader';
import { TicketInstruction } from './TicketInstruction/TicketInstruction';
import { Button, Heading, Icon, Text } from '@chakra-ui/react';
import { ReactComponent as close } from 'assets/close.svg';
import { FormattedMessage } from 'react-intl';
import { CircleDollarSignIcon } from 'lucide-react';

export const OrderSummaryPage = () => {
  const navigate = useNavigate();

  const search = useLocation().search;
  const orderID = new URLSearchParams(search).get('CartID') ?? '';
  const paymentID = new URLSearchParams(search).get('paymentId') ?? '';
  const { data, isFetching, isError } = useOrderSummaryQuery(orderID ? orderID : paymentID);

  useEffect(() => {
    if (!orderID && !paymentID) {
      navigate(ROUTES.home);
    }
  }, [orderID, navigate, paymentID]);

  // Google Tag Manager

  useEffect(() => {
    const dataLayer: unknown[] = (window as any).dataLayer || [];
    function gtag(...eventData: unknown[]) {
      dataLayer.push(eventData);
    }
    if (!isFetching && !isError && data?.payment_status === 'SUCCESS') {
      gtag('event', 'successful_order', {
        event_category: 'order',
        event_label: 'order_confirmation',
        value: 1,
      });
    }
  }, [data?.payment_status, isFetching, isError]);

  if (isFetching) {
    return <LoadingView />;
  }

  if (!isError && data?.payment_status === 'SUCCESS') {
    return (
      <>
        <SuccessHeader />
        <Grid
          mx='auto'
          maxW='container.xl'
          templateColumns={{ base: '1fr', md: '1fr 1fr' }}
          gap={{ base: '16px', md: '24px' }}
          p={{ base: '16px', md: '24px 16px 64px' }}
        >
          <GridItem>
            <TicketHolder tickets={data.tickets} />
          </GridItem>
          <GridItem gridRow={{ base: 1, md: 0 }}>
            <VStack gap='8px' align='stretch'>
              {/* <GameInfo {...data.item} /> */}
              <OrderDetails {...data} />
              <Show above='md'>
                <TicketInstruction />
              </Show>
            </VStack>
          </GridItem>
          <Show below='md'>
            <GridItem>
              <TicketInstruction />
            </GridItem>
          </Show>
        </Grid>
      </>
    );
  }

  if (data?.payment_status === 'FAILURE') {
    return (
      <VStack bg='green.G700' textAlign='center' justifyContent='center' gap='16px' p='40px 16px'>
        <Icon as={close} boxSize='80px' color='red.G500' />
        <div>
          <Heading
            as='h1'
            variant={{ base: 'headingMedium', sm: 'headingLarge' }}
            mb='4px'
            color='white'
          >
            <FormattedMessage
              id='orderSummary.error.title'
              defaultMessage='Error during placing an order'
            />
          </Heading>
          <Text textStyle='bodyLargeRegular' color='white'>
            <FormattedMessage
              id='orderSummary.error.descriptionFirstLine'
              defaultMessage='Unknown error happened during placing an order.'
            />
          </Text>
          <Show above='md'>
            <Text textStyle='bodyLargeRegular' color='white' mt='0px'>
              <FormattedMessage
                id='orderSummary.error.descriptionSecondLine'
                defaultMessage='Please try again later'
              />
            </Text>
          </Show>
        </div>
        <Button
          variant='primaryMedium'
          bg='green.G0'
          borderColor='green.G0'
          _hover={{ bg: 'green.G200', borderColor: 'green.G200' }}
          textColor='dark.D900'
          as={Link}
          to={ROUTES.home}
        >
          <FormattedMessage id='orderSummary.error.button' defaultMessage='Back to homepage' />
        </Button>
      </VStack>
    );
  }

  if (data?.payment_status === 'PENDING') {
    return (
      <VStack bg='green.G700' textAlign='center' justifyContent='center' gap='16px' p='40px 16px'>
        <Icon as={Spinner} boxSize='80px' color='yellow.500' />
        <div>
          <Heading
            as='h1'
            variant={{ base: 'headingMedium', sm: 'headingLarge' }}
            mb='4px'
            color='white'
          >
            <FormattedMessage
              id='orderSummary.pending.title'
              defaultMessage='Order is still processing'
            />
          </Heading>
          <Text textStyle='bodyLargeRegular' color='white'>
            <FormattedMessage
              id='orderSummary.pending.descriptionFirstLine'
              defaultMessage='Your order is still processing.'
            />
          </Text>
          <Show above='md'>
            <Text textStyle='bodyLargeRegular' color='white' mt='0px'>
              <FormattedMessage
                id='orderSummary.pending.descriptionSecondLine'
                defaultMessage='Please try again later'
              />
            </Text>
          </Show>
        </div>
        <Button
          variant='primaryMedium'
          bg='green.G0'
          borderColor='green.G0'
          _hover={{ bg: 'green.G200', borderColor: 'green.G200' }}
          textColor='dark.D900'
          as={Link}
          to={ROUTES.home}
        >
          <FormattedMessage id='orderSummary.pending.button' defaultMessage='Back to homepage' />
        </Button>
      </VStack>
    );
  }

  if (data?.payment_status === 'REFUNDED') {
    return (
      <VStack bg='green.G700' textAlign='center' justifyContent='center' gap='16px' p='40px 16px'>
        <Icon
          as={CircleDollarSignIcon}
          boxSize='80px'
          color='green.500'
          backgroundColor='white'
          borderRadius='full'
        />
        <div>
          <Heading
            as='h1'
            variant={{ base: 'headingMedium', sm: 'headingLarge' }}
            mb='4px'
            color='white'
          >
            <FormattedMessage
              id='orderSummary.refunded.title'
              defaultMessage='Order was refunded'
            />
          </Heading>
          <Text textStyle='bodyLargeRegular' color='white'>
            <FormattedMessage
              id='orderSummary.refunded.descriptionFirstLine'
              defaultMessage='Your order was refunded.'
            />
          </Text>
          <Show above='md'>
            <Text textStyle='bodyLargeRegular' color='white' mt='0px'>
              <FormattedMessage
                id='orderSummary.refunded.descriptionSecondLine'
                defaultMessage='Please try again later'
              />
            </Text>
          </Show>
        </div>
        <Button
          variant='primaryMedium'
          bg='green.G0'
          borderColor='green.G0'
          _hover={{ bg: 'green.G200', borderColor: 'green.G200' }}
          textColor='dark.D900'
          as={Link}
          to={ROUTES.home}
        >
          <FormattedMessage id='orderSummary.refunded.button' defaultMessage='Back to homepage' />
        </Button>
      </VStack>
    );
  }
  return null;
};
